import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRecommendedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const relatedComics = getRecommendedComics(4).filter((c) => c.id !== id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-[1400px] mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
            <span>/</span>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors duration-200">{comic.category}</Link>
            <span>/</span>
            <span className="text-foreground-900 font-medium">{comic.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="w-full max-w-[280px] mx-auto rounded-xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 亲亲漫画免费在线阅读`}
                    className="w-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 space-y-3">
                  <a
                    href="#chapters"
                    className="block w-full text-center py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                  >
                    开始阅读
                  </a>
                  <button className="block w-full text-center py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <div className="w-4 h-4 flex items-center justify-center inline-block mr-1.5 -mt-0.5 align-middle">
                      <i className="ri-bookmark-line"></i>
                    </div>
                    加入书架
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">{comic.title}</h1>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="text-sm text-foreground-600">
                  <strong className="text-foreground-900">作者：</strong>{comic.author}
                </span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">
                  <strong className="text-foreground-900">分类：</strong>
                  <Link to={`/category/${comic.categorySlug}`} className="text-primary-500 hover:text-primary-600 transition-colors duration-200">{comic.category}</Link>
                </span>
                <span className="text-xs text-foreground-400">|</span>
                <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-500' : 'text-foreground-500'}`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-foreground-500 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{(comic.views / 10000).toFixed(0)}万次阅读</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-foreground-500 text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{comic.chapters.length}话</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-8">
                <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div id="chapters">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">章节列表</h2>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium whitespace-nowrap cursor-pointer">
                      正序
                    </button>
                    <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                  </div>
                </div>

                <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2">
                  {comic.chapters.slice().reverse().map((chapter) => (
                    <Link
                      key={chapter.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-foreground-800 font-medium group-hover:text-primary-500 transition-colors duration-200">
                          {chapter.title}
                        </span>
                        {chapter.title === comic.latestChapter && (
                          <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-600 text-[10px] font-medium">
                            最新
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-foreground-400">{chapter.updateTime}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="pb-14">
              <div className="border-t border-background-200/70 pt-10">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5 md:gap-6" data-product-shop>
                  {relatedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} size="medium" />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}